import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Wallet, Clock, ShoppingBag, Repeat, Users, CalendarClock, Link2Off, ServerCrash, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eur = (n) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

const STATUS_LABELS = {
  paid: { label: 'Pagata', cls: 'bg-green-100 text-green-800' },
  unpaid: { label: 'Da pagare', cls: 'bg-amber-100 text-amber-800' },
  pending: { label: 'In attesa', cls: 'bg-gray-100 text-gray-700' },
  rejected: { label: 'Rifiutata', cls: 'bg-red-100 text-red-700' },
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card className="rounded-2xl border-gray-100 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
            <Icon className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <div>
            <p className="text-xs text-[#4a4238]">{label}</p>
            <p className="text-lg font-semibold text-[#12100c]">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CommissionsPage() {
  const [data, setData] = useState(null);
  const [riordini, setRiordini] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/me/commissions`)
      .then((resp) => setData(resp.data))
      .catch(() => setData({ linked: true, available: false }))
      .finally(() => setLoading(false));
    // Chiamata a parte: passa da WooCommerce e ci mette qualche secondo.
    axios
      .get(`${API}/me/riordini`)
      .then((resp) => setRiordini(resp.data))
      .catch(() => setRiordini({ linked: true, available: false }));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-[#12100c]">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const Header = (
    <div className="mb-6">
      <h1 className="font-heading text-2xl font-bold text-[#12100c]">Riordini e provvigioni</h1>
      <p className="text-sm text-[#4a4238] mt-1">
        Dati reali dal tuo account affiliato su biounica.it
      </p>
    </div>
  );

  if (!data?.linked) {
    return (
      <div>
        {Header}
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 bg-[#c6a15b]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Link2Off className="w-7 h-7 text-[#8a6620]" />
            </div>
            <h2 className="font-heading text-lg font-semibold text-[#12100c] mb-2">
              Nessun account affiliato collegato
            </h2>
            <p className="text-sm text-[#4a4238] max-w-md mx-auto">
              Questo account non risulta associato a un affiliato Biounica. Se sei un promotore,
              assicurati di usare la stessa email del tuo account su biounica.it.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data.available) {
    return (
      <div>
        {Header}
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <ServerCrash className="w-7 h-7 text-red-500" />
            </div>
            <h2 className="font-heading text-lg font-semibold text-[#12100c] mb-2">
              Dati momentaneamente non disponibili
            </h2>
            <p className="text-sm text-[#4a4238]">
              Non è stato possibile recuperare le statistiche da biounica.it. Riprova tra qualche minuto.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const referrals = data.recent_referrals || [];
  const r = riordini?.available ? riordini : null;

  return (
    <div>
      {Header}

      {/* I riordini vengono prima: sono la lettura che dice come sta andando.
          Se una persona ricompra, quel prodotto le sta facendo qualcosa. */}
      <Card className="rounded-2xl border-gray-100 shadow-sm mb-6" data-testid="riordini-card">
        <CardContent className="p-5 sm:p-6">
          <h2 className="font-heading text-lg font-semibold text-[#12100c]">
            Le persone che hai seguito
          </h2>
          <p className="text-sm text-[#4a4238] mt-1 mb-5 max-w-2xl leading-relaxed">
            Chi ricompra sta continuando: è il segnale più utile che abbiamo sul lavoro fatto.
            Sono numeri d'insieme, senza alcun dato delle singole persone.
          </p>

          {riordini === null ? (
            <div className="flex items-center gap-2 text-sm text-[#5b5346]">
              <Loader2 className="w-4 h-4 animate-spin" /> Sto contando i riordini...
            </div>
          ) : !r ? (
            <p className="text-sm text-[#4a4238]">
              Il conteggio dei riordini non è disponibile in questo momento.
            </p>
          ) : r.clienti === 0 ? (
            <p className="text-sm text-[#4a4238]">
              Ancora nessun ordine collegato al tuo codice.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Persone che hanno ordinato" value={r.clienti} accent="bg-[#12100c]/10 text-[#12100c]" />
                <StatCard icon={Repeat} label="Hanno riordinato" value={`${r.clienti_con_riordino} su ${r.clienti}`} accent="bg-green-100 text-green-700" />
                <StatCard icon={Repeat} label="Tasso di riordino" value={`${r.tasso_riordino}%`} accent="bg-green-100 text-green-700" />
                <StatCard
                  icon={CalendarClock}
                  label="Distanza media fra gli ordini"
                  value={r.giorni_medi_fra_ordini ? `${r.giorni_medi_fra_ordini} giorni` : 'n/d'}
                  accent="bg-blue-100 text-blue-700"
                />
              </div>
              <p className="text-xs text-[#5b5346] mt-4 leading-relaxed">
                {r.riordini} riordini su {r.ordini} ordini complessivi.
                {r.parziale && ` ${r.ordini_non_leggibili} ordini non sono più leggibili su biounica.it e restano fuori dal conteggio.`}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <h2 className="font-heading text-lg font-semibold text-[#12100c] mb-3">Le tue provvigioni</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Wallet} label="Maturate in tutto" value={eur(data.earnings)} accent="bg-green-100 text-green-700" />
        <StatCard icon={Clock} label="Ancora da liquidare" value={eur(data.unpaid_earnings)} accent="bg-amber-100 text-amber-700" />
        <StatCard icon={ShoppingBag} label="Le tue vendite" value={data.referrals_count} accent="bg-[#12100c]/10 text-[#12100c]" />
      </div>

      {/* I professionisti che hai presentato. Le loro vendite sono loro, non
          tue: qui compaiono solo le provvigioni che maturi sul loro lavoro. */}
      {(data.introduced_count > 0 || data.network_amount > 0) && (
        <Card className="rounded-2xl border-gray-100 shadow-sm mb-6" data-testid="rete-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-1">
              <UserPlus className="w-4 h-4 text-[#12100c]" strokeWidth={1.6} />
              <h2 className="font-heading text-lg font-semibold text-[#12100c]">
                I professionisti che hai presentato
              </h2>
            </div>
            <p className="text-sm text-[#4a4238] mb-4">
              Su ogni loro vendita maturi una provvigione, che si aggiunge alla tua senza togliere
              nulla a loro. Le vendite qui sotto sono le loro, e non entrano nel conteggio delle tue.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <StatCard
                icon={UserPlus}
                label="Professionisti presentati"
                value={data.introduced_count || 0}
                accent="bg-[#12100c]/10 text-[#12100c]"
              />
              <StatCard
                icon={ShoppingBag}
                label="Di cui hanno venduto"
                value={`${data.introduced_selling || 0} su ${data.introduced_count || 0}`}
                accent="bg-green-100 text-green-700"
              />
              <StatCard
                icon={Wallet}
                label="Provvigioni dal loro lavoro"
                value={eur(data.network_amount)}
                accent="bg-green-100 text-green-700"
              />
            </div>

            {data.introduced?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-[#4a4238] border-b border-gray-100">
                      <th className="py-2 pr-3 font-medium">Professionista</th>
                      <th className="py-2 px-3 font-medium text-right">Sue vendite</th>
                      <th className="py-2 pl-3 font-medium text-right">Provvigioni per te</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.introduced.map((sa) => (
                      <tr key={sa.affiliate_id} className="border-b border-gray-50 last:border-0">
                        <td className="py-2 pr-3 text-[#12100c]">
                          {sa.name || `Promotore #${sa.affiliate_id}`}
                          {!sa.active && (
                            <span className="ml-2 text-xs text-[#4a4238]">non più attivo</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">{sa.sales}</td>
                        <td className="py-2 pl-3 text-right tabular-nums font-medium">
                          {sa.commissions_for_me == null ? 'n/d' : eur(sa.commissions_for_me)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-[#12100c]">Ultime provvigioni</h2>
            {data.rate && (
              <span className="text-xs text-[#4a4238]">
                Commissione: {data.rate}{data.rate_type === 'percentage' ? '%' : ' EUR'}
              </span>
            )}
          </div>

          {referrals.length === 0 ? (
            <p className="text-sm text-[#4a4238] py-6 text-center">Ancora nessuna provvigione registrata.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-[#4a4238] border-b border-gray-100">
                    <th className="py-2 pr-3 font-medium">Data</th>
                    <th className="py-2 pr-3 font-medium">Descrizione</th>
                    <th className="py-2 pr-3 font-medium text-right">Importo</th>
                    <th className="py-2 font-medium text-right">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r) => {
                    const st = STATUS_LABELS[r.status] || { label: r.status, cls: 'bg-gray-100 text-gray-700' };
                    return (
                      <tr key={r.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-2.5 pr-3 text-[#4a4238] whitespace-nowrap">
                          {r.date ? String(r.date).slice(0, 10) : '-'}
                        </td>
                        <td className="py-2.5 pr-3 text-[#12100c] max-w-[320px] truncate">{r.description || '-'}</td>
                        <td className="py-2.5 pr-3 text-right font-medium text-[#12100c] whitespace-nowrap">
                          {eur(r.amount)}
                        </td>
                        <td className="py-2.5 text-right">
                          <Badge className={`${st.cls} border-0 font-medium`}>{st.label}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
